import { EApplicantType, EFinancingProgram, EHomeStatus, EProvince } from '@if/core/types'
import type { TranslationKey, TranslationOptions } from '@if/mui'
import { getAgeInYearsAndMonths } from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { ApplicantJob, ApplicantOtherIncome, CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { sumBy } from 'lodash-es'

// Errors carry an i18n key (+ params), not a pre-translated string, so callers can
// surface them through the design-system error system (`MultiTranslatedError`).
export interface BusinessError {
  messageKey: TranslationKey
  params?: TranslationOptions
}

function isHousingExpenseValid(homeFeeTypeId?: EHomeStatus | null, housingMonthly?: number | null) {
  return (
    homeFeeTypeId === EHomeStatus.Owner ||
    (housingMonthly != null &&
      ((homeFeeTypeId === EHomeStatus.WithRelative && housingMonthly >= 0) || housingMonthly > 0))
  )
}

function validateJobDurationAgainstAge(birthDate: string | Date, jobs: ApplicantJob[]): BusinessError[] {
  const errors: BusinessError[] = []
  const { ageMonths } = getAgeInYearsAndMonths(birthDate)

  jobs.forEach((job, index) => {
    const years = Number(job.years) || 0
    const months = Number(job.months) || 0
    if (years * 12 + months > ageMonths) {
      errors.push({
        messageKey: 'editCreditApplication.errors.timeAtJobExceedsAge',
        params: { index },
      })
    }
  })

  return errors
}

export function validateCreditApplicationCanBeSubmittedForReview(
  application: CreditApplication,
  selectedMerchant: Merchant | null,
): BusinessError[] {
  const ret: BusinessError[] = []
  const isSelectedMerchantActive = selectedMerchant?.isActive

  if (application.applicant?.birthDate && application.applicant.currentJobs?.length > 0) {
    ret.push(
      ...validateJobDurationAgainstAge(application.applicant.birthDate, application.applicant.currentJobs).map((e) => ({
        ...e,
        params: { ...e.params, applicantType: EApplicantType.Applicant },
      })),
    )
  }

  if (application.coApplicant?.birthDate && application.coApplicant.currentJobs?.length > 0) {
    ret.push(
      ...validateJobDurationAgainstAge(application.coApplicant.birthDate, application.coApplicant.currentJobs).map(
        (e) => ({
          ...e,
          params: { ...e.params, applicantType: EApplicantType.CoApplicant },
        }),
      ),
    )
  }

  const isProductAndNotQcApplicant =
    application.financingProgramId === EFinancingProgram.Products &&
    application.applicant.currentAddress.stateIso !== EProvince.Quebec

  const isProductAndNotQcCoApplicant =
    application.coApplicant &&
    application.financingProgramId === EFinancingProgram.Products &&
    application.coApplicant.currentAddress.stateIso !== EProvince.Quebec

  const hasCoapplicant = !!application?.coApplicant
  const applicantHousingExpensesValid =
    isHousingExpenseValid(
      application.applicant.expenses?.homeFeeTypeId,
      application.applicant.expenses?.housingMonthly,
    ) || isProductAndNotQcApplicant
  const coApplicantHousingExpensesValid =
    isHousingExpenseValid(
      application.coApplicant?.expenses?.homeFeeTypeId,
      application.coApplicant?.expenses?.housingMonthly,
    ) || isProductAndNotQcCoApplicant

  if (!isProductAndNotQcApplicant) {
    const applicantIncomes =
      Number(sumBy(application.applicant.currentJobs, (x: ApplicantJob) => x.annualSalary)) +
      Number(sumBy(application.applicant.otherIncomes, (x: ApplicantOtherIncome) => x.annualAmount))

    const coapplicantIncomes =
      Number(sumBy(application.coApplicant?.currentJobs, (x: ApplicantJob) => x.annualSalary)) +
      Number(sumBy(application.coApplicant?.otherIncomes, (x: ApplicantOtherIncome) => x.annualAmount))

    const programConfig = FinancingProgramConfigs[application.financingProgramId]

    if (
      applicantIncomes < programConfig.minIncome ||
      (hasCoapplicant && coapplicantIncomes < programConfig.minIncome)
    ) {
      ret.push({
        messageKey: 'editCreditApplication.errors.minIncomeNotMet',
        params: { minIncome: programConfig.minIncome },
      })
    }
  }
  if (applicantHousingExpensesValid === false || (hasCoapplicant && coApplicantHousingExpensesValid === false)) {
    ret.push({ messageKey: 'editCreditApplication.errors.expenses' })
  }
  if (!isSelectedMerchantActive) {
    ret.push({ messageKey: 'editCreditApplication.errors.merchantInactive' })
  }

  return ret
}
